import LoadingSpinner from "components/loading-spinner.tsx";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  clearContestState,
  fetchBetsForContestEntry,
  getContestsState,
  getCurrentEntryIdByContestId,
  getEntriesByContestId,
  setContestCurrentEntry,
  setNewContestEntryState
} from "state/slices/contests-slice.ts";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import useContestData from "hooks/useContestData.ts";
import { MyPicksPopout } from "components/contests/my-contests";
import ContestModal from "components/contests/contest-modal/contest-modal.tsx";
import { getGroupInitState, getGroupState, setGroup } from "state/slices/group-slice.ts";
import Header from "components/header";
import LoginButtons from "components/login-buttons";
import Headroom from "react-headroom";
import { useInovioSuccessfulPaymentRedirectHandler } from "hooks/useInovioSuccessfulPaymentRedirectHandler.ts";
import { Contest, isPickPerPoolContest } from "interfaces/leaderboard/contest.ts";
import ContestPageContestView from "components/contests/contest-details/header";
import { clearContestBanner, setContestBanner, } from "state/slices/adContestSlice.ts";
import { getContestPath, getGroupDetailsPath, getPoolsPath, getEventsPath, getEventGroupConfigsPath } from "utils/backend-path-builders.ts";
import { getRequest } from "utils/httpClient.ts";
import { EventsResponse } from "interfaces/events-response";
import { EventGroupConfig } from "interfaces/event-group-config";
import { toast } from "react-toastify";
import { Pool } from "interfaces/pool.ts";
import { NIL_GROUP_ID, POOL_STATUS } from "utils/constants.ts";
import ContestPostJoinInfoModal
  from "components/contests/contest-post-join-info-modal/contest-post-join-info-modal.tsx";
import HomepagePoolsList from "components/homepage-pools-list";
import { getIntegratedAppState, IntegratedAppState } from "state/slices/integrated-app-slice";
import { fetchBets } from "state/slices/bets-slice.ts";
import { isContestInGroup, getContestEntryFee } from "utils/contest-group-utils.ts";

const ContestPage = () => {
  const { id: contestId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Capture URL group_id on first render (before any navigation changes it)
  // Priority: group_id param > utm_campaign param (used by backend social share links)
  const urlGroupIdRef = useRef<string | null>(
    searchParams.get("group_id") || searchParams.get("utm_campaign")
  );

  const dispatch = useAppDispatch();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const groupId = useAppSelector(getGroupState).id;
  const groupInitState = useAppSelector(getGroupInitState);

  const integratedAppState = useAppSelector(getIntegratedAppState);

  const contestsState = useAppSelector(getContestsState);

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contestId));

  const contestEntries =
    useAppSelector((state) => getEntriesByContestId(state, contestId)) || [];

  const sortedContestEntriesOldestFirst =
    contestEntries.length > 0
      ? [...contestEntries].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      : [];

  const [showJoinedContestBanner, setShowJoinedContestBanner] = useState(false);

  const [pools, setPools] = useState<Pool[]>([]);
  const [loadingPools, setLoadingPools] = useState<boolean>(false);
  const [eventIconMap, setEventIconMap] = useState<Map<string, string>>(new Map());

  const navigate = useNavigate();

  const { loadingUserContests } = useContestData();

  const [groupChecked, setGroupChecked] = useState(false);

  const [loadingContest, setLoadingContest] = useState(false);
  const [contest, setContest] = useState<Contest>();

  const fetchPools = (skipLoading: boolean) => {
    if (!groupId || !contestId) {
      return;
    }
    const path = getPoolsPath(groupId, {
      contestId: contestId,
      pg: 1,
      page_size: 1000,
    });

    const sortPools = (pools: Pool[]) => {
      pools.sort((a, b) => {
        if (a.status !== b.status) {
          return a.status - b.status;
        }

        if (a.weight !== b.weight) {
          return b.weight - a.weight;
        }

        return a.close - b.close;
      });
    };

    // Only use loading state on initial page load
    if (!skipLoading) {
      setLoadingPools(true);
    }
    getRequest(path, { skipIntegrationApi: true })
      .then((response: Pool[]) => {
        const filteredPools = response.filter((pool) =>
          [POOL_STATUS.OPEN, POOL_STATUS.LIVE, POOL_STATUS.SETTLED].includes(
            pool.status
          )
        );

        sortPools(filteredPools);

        setPools(filteredPools);
      })
      .catch(() => toast.error("Something went wrong."))
      .finally(() => setLoadingPools(false));
  };

  // fetch pools
  useEffect(() => {
    if (contestId) {
      fetchPools(false);
    }
  }, [groupId, contestId]);

  // Fetch events and event group configs to build icon map for pool items
  useEffect(() => {
    if (!groupId) return;

    Promise.all([
      getRequest(getEventsPath(), {
        queryParams: { "group-id": groupId },
        skipIntegrationApi: true,
      }),
      getRequest(getEventGroupConfigsPath(), {
        queryParams: { "group-id": groupId },
        skipIntegrationApi: true,
      }),
    ])
      .then(([events, configs]: [EventsResponse[], EventGroupConfig[]]) => {
        const iconMap = new Map<string, string>();
        events.forEach((event) => {
          const eventConfig = configs.find((c) => c.event_id === event.id);
          // Prefer group-specific icon, fall back to global icon
          const iconUrl =
            eventConfig?.settings?.icon_url || event.settings?.icon_url;
          if (iconUrl) {
            iconMap.set(event.id, iconUrl);
          }
        });
        setEventIconMap(iconMap);
      })
      .catch((error) => {
        console.error("Error fetching event icons:", error);
      });
  }, [groupId]);

  useEffect(() => {
    if (!currentContestEntryId || integratedAppState.state === IntegratedAppState.UNDEFINED) {
      return;
    }

    dispatch(fetchBetsForContestEntry(currentContestEntryId));
  }, [currentContestEntryId, integratedAppState.state, dispatch]);

  // fetch contest
  useEffect(() => {
    if (!contestId) {
      return;
    }

    // In integrated mode, we don't use InitializeGroupState, so skip the initState check
    const isIntegratedMode = integratedAppState.state !== IntegratedAppState.NOT_INTEGRATED
      && integratedAppState.state !== IntegratedAppState.UNDEFINED;

    // Wait for group initialization to complete before validating group (non-integrated mode only)
    if (!isIntegratedMode && groupInitState !== "ready") {
      return;
    }

    setLoadingContest(true);
    // Use uuid.Nil as placeholder for groupId if not set (backend ignores it anyway)
    const effectiveGroupId = groupId || "00000000-0000-0000-0000-000000000000";
    getRequest(getContestPath(effectiveGroupId, contestId), { skipIntegrationApi: true })
      .then((contestById) => {
        setContest(contestById);

        // Check if contest has any groups
        if (!contestById.contest_groups || contestById.contest_groups.length === 0) {
          toast.error("Contest has no associated groups");
          navigate("/");
          return;
        }

        // Determine which group to use, in priority order:
        // 1. URL group_id param (if valid for this contest)
        // 2. Current group (if valid for this contest)
        // 3. Highest weight group from contest
        const urlGroupId = urlGroupIdRef.current;
        const isUrlGroupValid = urlGroupId && isContestInGroup(contestById, urlGroupId);
        const isCurrentGroupValid = isContestInGroup(contestById, groupId);

        let targetGroupId: string | null = null;

        if (isUrlGroupValid) {
          // URL group is valid - use it if different from current
          if (urlGroupId !== groupId) {
            targetGroupId = urlGroupId;
          }
        } else if (!isCurrentGroupValid) {
          // Current group not valid - switch to highest weight
          const sortedGroups = [...contestById.contest_groups].sort((a, b) => b.weight - a.weight);
          targetGroupId = sortedGroups[0].group_id;
        }

        if (targetGroupId && targetGroupId !== NIL_GROUP_ID) {
          // Need to switch groups
          getRequest(getGroupDetailsPath(targetGroupId), { skipIntegrationApi: true })
            .then((groupData) => {
              dispatch(setGroup(groupData));
              setGroupChecked(true);
            })
            .catch(() => {
              toast.error("Unable to load contest group");
              navigate("/");
            });
        } else {
          // Keep current group (or nil group)
          setGroupChecked(true);
        }
      })
      .catch(() => toast.error("Something went wrong."))
      .finally(() => setLoadingContest(false));
  }, [contestId, contestsState.contestsStale, groupId, groupInitState, integratedAppState.state, navigate, dispatch]);

  // joined banner state
  useEffect(() => {
    const newContestEntry = contestsState.newContestEntry;
    if (newContestEntry) {
      setShowJoinedContestBanner(!newContestEntry.bannerHasBeenShown);
    } else {
      setShowJoinedContestBanner(false);
    }
  }, [contestsState.newContestEntry]);

  useEffect(() => {
    if (contestId && sortedContestEntriesOldestFirst.length) {
      const latestEntry =
        sortedContestEntriesOldestFirst[
        sortedContestEntriesOldestFirst.length - 1
          ];
      dispatch(setContestCurrentEntry({ contestId: contestId, entryId: latestEntry.id }));
    }
  }, [sortedContestEntriesOldestFirst.length, contestId, loadingUserContests]);

  // joined banner state
  useEffect(() => {
    if (contest && contest.settings?.ad_banner?.enabled) {
      dispatch(setContestBanner(contest.settings.ad_banner));
    }

    // Cleanup function to clear the contest banner when unmounting
    return () => {
      dispatch(clearContestBanner());
    };
  }, [contest, dispatch]);

  // fetching bets for contest entry
  useEffect(() => {
    if (!groupId || !currentContestEntryId || integratedAppState.state === IntegratedAppState.UNDEFINED) {
      return;
    }

    dispatch(
      fetchBets({
        groupId,
        contestEntryId: currentContestEntryId!!,
        isIntegratedApp: integratedAppState.state !== IntegratedAppState.NOT_INTEGRATED,
      })
    );
  }, [groupId, currentContestEntryId, integratedAppState.state, dispatch]);

  useInovioSuccessfulPaymentRedirectHandler({
    whenRedirectedFromPaymentPage: () => {
      setShowDetailsModal(true);
    },
  });

  if (
    ((loadingContest || loadingUserContests) && !contest) ||
    !groupChecked
  ) {
    return <LoadingSpinner />;
  } else if (!contest) {
    navigate("/");
    return;
  }

  return (
    <>
      <Headroom>
        <Header />
        <LoginButtons />
      </Headroom>

      {contest && <ContestPageContestView
        contest={contest}
        userContestEntries={sortedContestEntriesOldestFirst}
        setShowModal={setShowDetailsModal}
      />}

      {loadingPools && <LoadingSpinner />}

      {!loadingPools && (
        <HomepagePoolsList
          refetchPoolData={() => fetchPools(true)}
          pools={pools}
          isContestPool={true}
          contestEntryId={currentContestEntryId}
          eventIconMap={eventIconMap}
        />
      )}

      {currentContestEntryId && (<MyPicksPopout contestEntryId={currentContestEntryId} />)}

      <ContestModal
        contest={contest}
        show={showDetailsModal}
        setShow={setShowDetailsModal}
      />

      <ContestPostJoinInfoModal
        show={showJoinedContestBanner}
        entryFee={getContestEntryFee(contest, groupId)}
        onHide={() => {
          const newContestEntry = contestsState.newContestEntry;
          if (newContestEntry) {
            dispatch(setNewContestEntryState({
              id: newContestEntry.id,
              balanceHasBeenAnimated: newContestEntry.balanceHasBeenAnimated,
              bannerHasBeenShown: true
            }));
          }
        }}
        initialBalance={contest?.initial_reward_amount ?? 1000}
        contestType={contest?.type}
        isPickPerPool={isPickPerPoolContest(contest)}
      />
    </>
  );
};

export default ContestPage;
